// Dev rejimida so'rovlar CRA proxy orqali (package.json "proxy") bir xil origin'dan ketadi; prod'da REACT_APP_API_URL bering
export const BASE_URL = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'development' ? '' : 'http://localhost:8080');

// ─── Token helpers ───────────────────────────────────────────
export const getToken = () => localStorage.getItem('accessToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');
export const getUserId = () => localStorage.getItem('userId');
export const isLoggedIn = () => !!getToken();
export const getRole = () => localStorage.getItem('role');
export const setRole = (role) => role ? localStorage.setItem('role', role) : localStorage.removeItem('role');
export const isAdmin = () => isLoggedIn() && getRole() === 'ADMIN';

export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
};

export const clearAuth = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('role');
};

export const setUserId = (id) => localStorage.setItem('userId', id);

// ─── Token refresh ────────────────────────────────────────────
// Parallel 401 javoblarida bitta refresh so'rovi ishlatiladi (backend refresh tokenni rotate qiladi)
let refreshPromise = null;
const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

const doRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE_URL}/user/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) { clearAuth(); return false; }
    const data = await res.json();
    if (data.element) {
      setTokens(data.element.accessToken, data.element.refreshToken);
      return true;
    }
  } catch { /* ignore */ }
  clearAuth();
  return false;
};

// ─── Core request ─────────────────────────────────────────────
const request = async (url, options = {}, retry = true) => {
  const token = getToken();
  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let res;
  try {
    res = await fetch(`${BASE_URL}${url}`, { ...options, headers });
  } catch {
    return null; // tarmoq xatosi
  }

  if (res.status === 401 && retry) {
    const ok = await refreshAccessToken();
    if (ok) return request(url, options, false);
    clearAuth();
    window.location.href = '/login';
    return null;
  }

  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
};

// ─── Image URL helper ─────────────────────────────────────────
export const imageUrl = (fileName) =>
  fileName ? `${BASE_URL}/images/${fileName}` : null;

/**
 * Kartalar uchun kichraytirilgan nusxa: nginx /images/w400|w600|w800/ (image_filter + kesh) beradi.
 * Asl rasm 50–330 KB, karta eni ~300px — kichik nusxa 2–6 baravar yengil. cssWidth × ekran zichligi bo'yicha:
 * oddiy ekran → 400, ko'pchilik telefonlar (2×) → 600, 3× ekranlar → 800. Asl rasm faqat galereyada (imageUrl).
 */
export const thumbUrl = (fileName, cssWidth = 320) => {
  if (!fileName) return null;
  const px = cssWidth * (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  return `${BASE_URL}/images/w${px <= 420 ? 400 : px <= 680 ? 600 : 800}/${fileName}`;
};

// ─── API methods ──────────────────────────────────────────────
export const api = {
  // Auth
  login: (email, password) =>
    request('/user/login', { method: 'POST', body: JSON.stringify({ email, password }) }, false),

  googleLogin: (credential) =>
    request('/user/google', { method: 'POST', body: JSON.stringify({ credential }) }, false),

  register: (data) =>
    request('/user/register', { method: 'POST', body: JSON.stringify(data) }, false),

  checkEmail: (email, sendedPassword) =>
    request(`/user/checkEmail?email=${encodeURIComponent(email)}&sendedPassword=${sendedPassword}`, {
      method: 'POST',
    }, false),

  logout: () => request('/user/logout', { method: 'POST' }, false),

  // Favorites
  toggleFavoriteAd: (adId) => request(`/favorites/ads/${adId}`, { method: 'POST' }),
  getFavoriteAds: (page = 0, size = 50) => request(`/favorites/ads?page=${page}&size=${size}`),
  toggleFavoritePartnerAd: (adId) => request(`/favorites/partner-ads/${adId}`, { method: 'POST' }),
  getFavoritePartnerAds: (page = 0, size = 50) => request(`/favorites/partner-ads?page=${page}&size=${size}`),

  forgotPasswordSend: (email) =>
    request(`/user/forgot-password/send?email=${encodeURIComponent(email)}`, { method: 'POST' }, false),

  forgotPasswordReset: (email, code, newPassword) =>
    request(`/user/forgot-password/reset?email=${encodeURIComponent(email)}&code=${code}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: newPassword,
    }, false),

  // User
  getUser: () => request('/user'),

  updateUser: (id, data) =>
    request(`/user/update/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  changePassword: (password) =>
    request('/user/changePassword', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: password,
    }),

  saveLocation: (latitude, longitude) =>
    request(`/user/location?latitude=${latitude}&longitude=${longitude}`, { method: 'POST' }),

  uploadPicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/user/uploadPicture', { method: 'POST', body: formData });
  },

  // Ads (Ijara uy)
  getMyAds: () => request('/ad/my'),
  // E'lon egasi: natijalar (ko'rishlar, raqam ochishlar, narx bozorga nisbatan) va «Ijarachi topildi»
  getOwnerStats: () => request('/owner/stats'),
  markRented: (type, id) => request(`/owner/${type}/${id}/rented`, { method: 'POST' }),
  // Uy egasi kabineti: ijaralar, to'lov kalendari
  createTenancy: (data) => request('/tenancy', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  getMyTenancies: () => request('/tenancy/my'),
  setTenancyPaid: (id, period, paid) => request(`/tenancy/${id}/payments/${period}?paid=${paid ? 'true' : 'false'}`, { method: 'PUT' }),
  endTenancy: (id) => request(`/tenancy/${id}/end`, { method: 'POST' }),
  extendTenancy: (id, months = 12) => request(`/tenancy/${id}/extend?months=${months}`, { method: 'POST' }),
  relistTenancy: (id) => request(`/tenancy/${id}/relist`, { method: 'POST' }),
  inviteTenant: (id) => request(`/tenancy/${id}/invite`, { method: 'POST' }),
  setUtilitiesPayer: (id, payer) => request(`/tenancy/${id}/utilities-payer?payer=${payer || ''}`, { method: 'PUT' }),
  writeReference: (id, data) => request(`/tenancy/${id}/reference`, { method: 'POST', body: JSON.stringify(data) }),
  getLeaseReport: (year) => request(`/tenancy/report?year=${year}`),
  // Ijarachi tomoni
  getMyTenantLeases: () => request('/tenancy/tenant'),
  getLeaseInvite: (code) => request(`/tenancy/invite/${encodeURIComponent(code)}`),
  answerLeaseInvite: ({ id, code, yes }) => request(`/tenancy/answer?yes=${yes ? 'true' : 'false'}${id ? `&id=${id}` : ''}${code ? `&code=${encodeURIComponent(code)}` : ''}`, { method: 'POST' }),
  claimRentPaid: (id, period) => request(`/tenancy/${id}/claim/${period}`, { method: 'POST' }),
  hideReference: (id, hidden) => request(`/tenancy/${id}/reference-hidden?hidden=${hidden ? 'true' : 'false'}`, { method: 'PUT' }),
  // Kommunal va ta'mir (ega ham, ijarachi ham)
  getUtilityBills: (id) => request(`/tenancy/${id}/utilities`),
  addUtilityBill: (id, fd) => request(`/tenancy/${id}/utilities`, { method: 'POST', body: fd }),
  setUtilityPaid: (billId, paid) => request(`/tenancy/utilities/${billId}/paid?paid=${paid ? 'true' : 'false'}`, { method: 'PUT' }),
  deleteUtilityBill: (billId) => request(`/tenancy/utilities/${billId}`, { method: 'DELETE' }),
  getRepairs: (id) => request(`/tenancy/${id}/repairs`),
  addRepair: (id, fd) => request(`/tenancy/${id}/repairs`, { method: 'POST', body: fd }),
  setRepairStatus: (repairId, status) => request(`/tenancy/repairs/${repairId}/status?status=${status}`, { method: 'PUT' }),
  // Ijarachi anketasi va e'lon egasiga yuborish
  getAnketa: () => request('/renter/anketa'),
  saveAnketa: (data) => request('/renter/anketa', { method: 'PUT', body: JSON.stringify(data) }),
  sendAnketa: (adType, adId, message) => request('/renter/apply', { method: 'POST', body: JSON.stringify({ adType, adId, message }) }),
  getSentAnketas: () => request('/renter/sent'),
  getApplications: () => request('/renter/applications'),
  getApplicantContact: (id) => request(`/renter/applications/${id}/contact`),
  getPriceTrend: (p = {}) => request(`/stats/price-trend?${new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null && v !== '')))}`),
  // "Uy qidiryapman" e'lonlari
  getWanted: (p = {}) => request(`/wanted?${new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null && v !== '')))}`),
  getMyWanted: () => request('/wanted/my'),
  createWanted: (d) => request('/wanted', { method: 'POST', body: JSON.stringify(d) }),
  updateWanted: (id, d) => request(`/wanted/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteWanted: (id) => request(`/wanted/${id}`, { method: 'DELETE' }),
  sendWantedOffer: (id, d) => request(`/wanted/${id}/offer`, { method: 'POST', body: JSON.stringify(d) }),
  getContact: (type, id) => request(`/contact/${type}/${id}`),
  // BuddyUp
  getBuddyGroups: (adId) => request(`/buddy/ad/${adId}`),
  createBuddyGroup: (adId, d) => request(`/buddy/ad/${adId}`, { method: 'POST', body: JSON.stringify(d) }),
  joinBuddy: (gid) => request(`/buddy/${gid}/join`, { method: 'POST' }),
  leaveBuddy: (gid) => request(`/buddy/${gid}/leave`, { method: 'POST' }),
  tgStatus: () => request('/telegram/me'),
  tgLink: (lang) => request('/telegram/link', { method: 'POST', body: JSON.stringify({ lang }) }),
  tgSettings: (body) => request('/telegram/settings', { method: 'PUT', body: JSON.stringify(body) }),
  tgTest: () => request('/telegram/test', { method: 'POST' }),
  tgUnlink: () => request('/telegram', { method: 'DELETE' }),
  // Ro'yxatdan o'tmasdan: filtrlar → t.me havolasi (obuna botda /start bosilganda yaratiladi)
  tgGuest: (body) => request('/telegram/guest', { method: 'POST', body: JSON.stringify(body) }),
  getPriceInsight: (id, partner = false) => request(`/${partner ? 'ad-partner' : 'ad'}/${id}/price-insight`),
  getAllAds: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request(`/ad/search${q ? '?' + q : ''}`);
  },
  // Hududdagi faol e'lonlar soni tumanlar bo'yicha: [{ district, count }]
  getAdDistrictCounts: (region) => request(`/ad/district-counts?region=${encodeURIComponent(region)}`),
  // Hudud landing sahifasi: URL → filtr + matn + ichki havolalar
  getLanding: (path) => request(`/seo/landing?path=${encodeURIComponent(path)}`),
  // «Maslahatlar» blogi
  getBlog: () => request('/blog'),
  getBlogPost: (slug) => request(`/blog/${encodeURIComponent(slug)}`),
  getPartnerAdDistrictCounts: (region) => request(`/ad-partner/district-counts?region=${encodeURIComponent(region)}`),
  getAdById: (id) => request(`/ad/${id}`),
  addAd: (data) =>
    request('/ad/add', { method: 'POST', body: JSON.stringify(data) }),
  updateAd: (id, data) =>
    request(`/ad/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  changeAdStatus: (id) =>
    request(`/ad/changeStatus?id=${id}`, { method: 'POST' }),
  deleteAdImage: (id, fileName) => request(`/ad/${id}/image?fileName=${encodeURIComponent(fileName)}`, { method: 'DELETE' }),
  uploadAdImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/ad/${id}/uploadImage`, { method: 'POST', body: formData });
  },

  // Partner Ads (Sherik)
  getMyPartnerAds: () => request('/ad-partner/my'),
  // Erkin matn → filtrlar («2 ta yigit talabaga Chilonzorda» → tenant, people, district...)
  parseQuery: (q) => request(`/search/parse?q=${encodeURIComponent(q)}`),
  getAllPartnerAds: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request(`/ad-partner/search${q ? '?' + q : ''}`);
  },
  getPartnerAdById: (id) => request(`/ad-partner/${id}`),
  addPartnerAd: (data) =>
    request('/ad-partner/add', { method: 'POST', body: JSON.stringify(data) }),
  updatePartnerAd: (id, data) =>
    request(`/ad-partner/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  changePartnerAdStatus: (id) =>
    request(`/ad-partner/changeStatus?id=${id}`, { method: 'POST' }),
  deletePartnerAdImage: (id, fileName) => request(`/ad-partner/${id}/image?fileName=${encodeURIComponent(fileName)}`, { method: 'DELETE' }),
  uploadPartnerAdImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request(`/ad-partner/${id}/uploadImage`, { method: 'POST', body: formData });
  },
  // Shikoyatlar
  createReport: (data) => request('/reports', { method: 'POST', body: JSON.stringify(data) }),
  adminGetReports: (status, page = 0, size = 20, q = '') => request(`/admin/reports?status=${status || ''}&page=${page}&size=${size}&q=${encodeURIComponent(q)}`),
  adminSetReportStatus: (id, status) => request(`/admin/reports/${id}/status?status=${status}`, { method: 'PUT' }),
  adminDeleteAd: (id) => request(`/admin/ads/${id}`, { method: 'DELETE' }),
  adminDeletePartnerAd: (id) => request(`/admin/partner-ads/${id}`, { method: 'DELETE' }),
  getRecommendedAds: () => request('/recommendation/ads'),
  getRecommendedPartnerAds: () => request('/recommendation/partner-ads'),
  createViewing: (data) => request('/viewings', { method: 'POST', body: JSON.stringify(data) }),
  getIncomingViewings: () => request('/viewings/incoming'),
  getOutgoingViewings: () => request('/viewings/outgoing'),
  setViewingStatus: (id, status) => request(`/viewings/${id}/status?status=${status}`, { method: 'PUT' }),
  createSavedSearch: (data) => request('/saved-searches', { method: 'POST', body: JSON.stringify(data) }),
  getMySavedSearches: () => request('/saved-searches/my'),
  deleteSavedSearch: (id) => request(`/saved-searches/${id}`, { method: 'DELETE' }),
  getUserRating: (userId) => request(`/reviews/user/${userId}/rating`),
  getUserReviews: (userId) => request(`/reviews/user/${userId}?size=5`),
  createUserReview: (userId, data) => request(`/reviews/user/${userId}`, { method: 'POST', body: JSON.stringify(data) }),
  getSiteReviews: (size = 12) => request(`/site-reviews?size=${size}`),
  saveSiteReview: (data) => request('/site-reviews', { method: 'POST', body: JSON.stringify(data) }),
  deleteSiteReview: (id) => request(`/site-reviews/${id}`, { method: 'DELETE' }),
  createExpense: (data) => request('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  getMyExpenses: () => request('/expenses/my'),
  toggleExpenseShare: (id) => request(`/expenses/shares/${id}/paid`, { method: 'PUT' }),
  deleteExpense: (id) => request(`/expenses/${id}`, { method: 'DELETE' }),
  adminGetStats: () => request('/admin/stats'),
  adminGetVisitors: () => request('/admin/stats/visitors'),
  adminTgStats: () => request('/admin/telegram/stats'),
  adminSources: (days = 14) => request(`/admin/stats/sources?days=${days}`),
  adminTgUsers: (q = '') => request(`/admin/telegram/users?q=${encodeURIComponent(q)}`),
  adminGetTimeseries: (metric, days = 30) => request(`/admin/stats/timeseries?metric=${metric}&days=${days}`),
  adminGetUsers: (page = 0, size = 20, q = '') => request(`/admin/users?page=${page}&size=${size}&q=${encodeURIComponent(q)}`),
  adminSetRole: (id, role) => request(`/admin/users/${id}/role?role=${role}`, { method: 'PUT' }),
  adminSetVerified: (id, value) => request(`/admin/users/${id}/verified?value=${value}`, { method: 'PUT' }),
  adminDeleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  adminGetAds: (page = 0, size = 20, q = '') => request(`/admin/ads?page=${page}&size=${size}&q=${encodeURIComponent(q)}`),
  adminGetPartnerAds: (page = 0, size = 20, q = '') => request(`/admin/partner-ads?page=${page}&size=${size}&q=${encodeURIComponent(q)}`),
  adminPromoteAd: (id, days) => request(`/admin/ads/${id}/promote?days=${days}`, { method: 'PUT' }),
  adminPromotePartnerAd: (id, days) => request(`/admin/partner-ads/${id}/promote?days=${days}`, { method: 'PUT' }),
  setGoal: (goal) => request(`/user/goal?goal=${goal}`, { method: 'PUT' }),
};

// ─── Anonim tashrif statistikasi (login shart emas) ────────────
const visitorId = () => {
  try {
    let v = localStorage.getItem('visitorId');
    if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)); localStorage.setItem('visitorId', v); }
    return v;
  } catch { return null; }
};
// Admin qurilmasi umuman yubormaydi (bir marta admin bo'lib kirgan brauzer — logout'dan keyin ham).
// Botlar va avtomatlashtirilgan brauzerlar yuboradi, lekin server ularni statistikaga emas, «Botlar» hisobiga yozadi
// (User-Agent bo'yicha; wd — navigator.webdriver: Playwright/Selenium/Puppeteer skriptlari)
const noTrack = () => {
  try {
    if (localStorage.getItem('role') === 'ADMIN') localStorage.setItem('ijm.noTrack', '1');
    return localStorage.getItem('ijm.noTrack') === '1';
  } catch { return false; }
};
const isAutomated = () => typeof navigator !== 'undefined' && !!navigator.webdriver;
/**
 * Sessiya manbasi (birinchi sahifa ochilganda aniqlanadi, sessiya davomida o'zgarmaydi): utm_source[:utm_campaign] →
 * bizning havolalar (?src=tg — bot xabari, ?src=tg_kanal — kanal) → fbclid (Instagram/FB reklama) → referrer domeni → direct.
 * Telegram ichki brauzeri referrer bermaydi — shuning uchun bot/kanal havolalarida ?src bor. Admin «Manbalar» jadvali shundan.
 */
const sessionSource = () => {
  try {
    const saved = sessionStorage.getItem('ijm.src');
    if (saved) return saved;
    const q = new URLSearchParams(window.location.search);
    const clean = (v, n) => (v || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, n);
    const utm = clean(q.get('utm_source'), 20), camp = clean(q.get('utm_campaign'), 15), own = clean(q.get('src'), 12);
    let ref = '';
    try { ref = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '').toLowerCase() : ''; } catch { ref = ''; }
    let s;
    if (utm) s = camp ? `${utm}:${camp}` : utm;
    else if (own === 'tg') s = 'tg_bot';
    else if (own === 'tg_kanal') s = 'tg_kanal';
    else if (q.get('fbclid')) s = 'meta_ad';
    else if (!ref || ref.endsWith('myijarachi.uz')) s = 'direct';
    else if (/(^|\.)google\./.test(ref)) s = 'google';
    else if (/(^|\.)yandex\./.test(ref)) s = 'yandex';
    else if (/instagram\./.test(ref)) s = 'instagram';
    else if (/(^|\.)(facebook|fb)\./.test(ref)) s = 'facebook';
    else if (/(^|\.)t\.me$|telegram/.test(ref)) s = 'telegram';
    else s = 'ref:' + ref.slice(0, 30);
    s = s.replace(/[^a-z0-9_:.-]/g, '').slice(0, 40) || 'direct';
    sessionStorage.setItem('ijm.src', s);
    return s;
  } catch { return undefined; }
};
export const trackVisit = (path) => {
  if (noTrack()) return;
  const id = visitorId(); if (!id) return;
  const body = JSON.stringify({ visitorId: id, path, userId: getUserId() || undefined, wd: isAutomated() ? '1' : undefined, src: sessionSource() });
  fetch(`${BASE_URL}/visit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
};
/**
 * Foydalanuvchi harakati — voronka tahlili uchun (admin «Hodisalar»). Nomlar backend ro'yxatida bo'lishi shart:
 * phone_reveal, phone_limit, call_click, telegram_click, filter_apply, more_filters, home_filter, search,
 * login_ok, login_error, register_ok, post_start, post_step, post_login_wall, post_submit
 */
export const trackEvent = (name, detail = '') => {
  if (noTrack()) return;
  const id = visitorId(); if (!id) return;
  // Token yuborilmaydi: muddati o'tgan token hodisani 401 bilan yo'qotmasin (userId — faqat statistika uchun)
  const body = JSON.stringify({ visitorId: id, name, userId: getUserId() || undefined, wd: isAutomated() ? '1' : undefined, detail: String(detail).replace(/[^A-Za-z0-9_:.,=-]/g, '').slice(0, 60) });
  fetch(`${BASE_URL}/visit/event`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
};
